import { HugeiconsIcon } from '@hugeicons/react'
import {
  Building02Icon,
  Mail01Icon,
  UserCheck01Icon,
  UserIcon,
} from '@hugeicons/core-free-icons'
import type { OrganizationWithRoles, AuthUser } from '../types'
import { AvatarWithInitials } from '@/components/common/avatar-with-initials'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'

interface UserProfileProps {
  user: AuthUser
  organizations: Array<OrganizationWithRoles>
  currentOrganizationId?: string
  manager?: {
    id: string
    name: string
  } | null
  position?: {
    title: string
    role: string
    level: string
  } | null
}

export function UserProfile({
  user,
  organizations,
  currentOrganizationId,
  manager,
  position,
}: UserProfileProps) {
  return (
    <div className="w-full h-full p-6 bg-muted/30 rounded-lg flex flex-col">
      <div className="pb-6 text-center">
        <div className="flex flex-col items-center space-y-4">
          <div className="relative">
            <AvatarWithInitials
              name={user.name || user.email || 'User'}
              src={user.image ?? undefined}
              className="h-24 w-24"
              fallbackClassName="text-xl font-semibold"
            />
          </div>

          <div className="space-y-1">
            <h2 className="text-2xl font-bold tracking-tight">
              {user.name || 'User'}
            </h2>
            {user.email && (
              <div className="flex items-center justify-center gap-2 text-muted-foreground">
                <HugeiconsIcon icon={Mail01Icon} className="h-4 w-4" />
                <span className="text-sm">{user.email}</span>
                <Badge variant="secondary" className="text-xs px-2">
                  Verified
                </Badge>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="flex-1 flex flex-col gap-6 min-h-0">
        {/* Position */}
        {position && (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <HugeiconsIcon
                icon={UserIcon}
                className="h-4 w-4 text-muted-foreground"
              />
              <h3 className="font-semibold text-base">Position</h3>
            </div>
            <div className="rounded-lg p-3 bg-muted/30">
              <p className="font-medium text-sm">{position.title}</p>
              <p className="text-xs text-muted-foreground">
                {position.role} - {position.level}
              </p>
            </div>
          </div>
        )}

        {/* Manager */}
        {manager && (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <HugeiconsIcon
                icon={UserCheck01Icon}
                className="h-4 w-4 text-muted-foreground"
              />
              <h3 className="font-semibold text-base">Manager</h3>
            </div>
            <div className="rounded-lg p-3 bg-muted/30">
              <p className="font-medium text-sm">{manager.name}</p>
            </div>
          </div>
        )}

        {/* Organizations — scrollable if overflow */}
        {organizations.length > 0 && (
          <div className="flex flex-col gap-3 min-h-0 flex-1">
            <div className="flex items-center gap-2">
              <HugeiconsIcon
                icon={Building02Icon}
                className="h-4 w-4 text-muted-foreground"
              />
              <h3 className="font-semibold text-base">Organizations</h3>
            </div>

            <div className="overflow-y-auto space-y-3 pr-1">
              {organizations.map((org) => {
                const isCurrentOrg = currentOrganizationId === org.id

                return (
                  <div
                    key={org.id}
                    className={cn(
                      'rounded-lg p-4 border-2 transition-all',
                      isCurrentOrg
                        ? 'border-primary/50 bg-primary/5'
                        : 'border-muted bg-muted/20',
                    )}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <h4 className="font-medium">{org.name}</h4>
                        {isCurrentOrg && (
                          <Badge variant="default" className="text-xs">
                            Current
                          </Badge>
                        )}
                      </div>
                    </div>

                    {org.description && (
                      <p className="text-xs text-muted-foreground mb-2">
                        {org.description}
                      </p>
                    )}

                    {org.roles.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {org.roles.map((role: string) => (
                          <Badge
                            key={role}
                            variant={isCurrentOrg ? 'default' : 'secondary'}
                            className="text-xs"
                          >
                            {role.charAt(0).toUpperCase() + role.slice(1)}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        )}

        {/* User ID — pinned to bottom */}
        <div className="mt-auto flex items-center justify-between text-xs text-muted-foreground bg-muted/30 rounded-lg p-3">
          <div className="flex items-center gap-2">
            <HugeiconsIcon icon={UserIcon} className="h-3 w-3" />
            <span>User ID</span>
          </div>
          <span className="font-mono truncate max-w-[160px] text-right">
            {user.userId}
          </span>
        </div>
      </div>
    </div>
  )
}
